import GeoJSON from 'geojson'

import { UniqueEntityID } from '@/domain/core'
import { Navaid, NavaidExtra, NavaidPurpose, NavaidType } from '@/domain/models/Waypoint'
import { Coordinates } from '@/domain/protocols/Coordinates'

// O FlatRecord emite 'VOR_DME'; outras fontes usam 'VOR-DME'/'VOR/DME'. Desconhecido → 'NDB'.
export const normalizeNavaidType = (raw: unknown): NavaidType => {
  const value = String(raw ?? '')
    .trim()
    .toUpperCase()
    .replace(/[-_\s]+/g, '/')

  switch (value) {
    case 'VOR/DME':
      return 'VOR/DME'
    case 'DME':
      return 'DME'
    case 'VOR':
      return 'VOR'
    case 'NDB':
    default:
      return 'NDB'
  }
}

const normalizePurpose = (raw: unknown): NavaidPurpose | undefined => {
  const value = String(raw ?? '')
    .trim()
    .toUpperCase()
  if (value === 'ENROUTE') return 'ENROUTE'
  if (value === 'TERMINAL') return 'TERMINAL'
  return undefined
}

const toTrimmedString = (value: unknown): string | undefined => {
  if (value === undefined || value === null) return undefined
  const str = String(value).trim()
  return str.length > 0 ? str : undefined
}

// Escolhe o texto de remarks para `deviceLanguage` (2 letras, ex.: 'pt'): o `remarks`
// localizado quando o idioma bate, senão `remarks_en`, senão `remarks`.
export const resolveRemarks = (properties: Record<string, unknown>, deviceLanguage: string): string | undefined => {
  const remarks = toTrimmedString(properties.remarks)
  const remarksLang = toTrimmedString(properties.remarks_lang)
  const remarksEn = toTrimmedString(properties.remarks_en)

  if (remarksLang && remarksLang.slice(0, 2).toLowerCase() === deviceLanguage.slice(0, 2).toLowerCase()) {
    return remarks
  }
  return remarksEn ?? remarks
}

// Mapeia uma feature do FlatRecord (Point GeoJSON + colunas de navaid) para um Navaid,
// ou null quando a geometria é inutilizável / o mapeamento falha.
export const mapFeatureToNavaid = (feature: GeoJSON.Feature, deviceLanguage = 'pt'): Navaid | null => {
  try {
    if (!feature.geometry || feature.geometry.type !== 'Point') return null
    const [longitude, latitude] = feature.geometry.coordinates as [number, number]
    if (typeof longitude !== 'number' || typeof latitude !== 'number') return null

    const properties = (feature.properties ?? {}) as Record<string, unknown>

    const code = toTrimmedString(properties.designator) ?? ''
    const name = toTrimmedString(properties.name) ?? ''
    const type = normalizeNavaidType(properties.type)
    const frequency = (properties.frequency_value as number) ?? 0

    const extra: NavaidExtra = {
      type,
      frequency,
      frequencyUom: toTrimmedString(properties.frequency_uom),
      purpose: normalizePurpose(properties.purpose),
      operationalHours: toTrimmedString(properties.operational_hours),
      remarks: resolveRemarks(properties, deviceLanguage)
    }

    return Navaid.create(
      {
        code,
        name,
        coordinates: Coordinates.create({ latitude, longitude })
      },
      extra,
      new UniqueEntityID(toTrimmedString(properties.id))
    )
  } catch (error) {
    console.error(`[NavaidFlatRecordMapper] ${error}`)
    return null
  }
}
